import { sortAlpha } from '../../utils';

const DEFAULT_BLOGS_PER_PAGE = 9;

async function fetchPost(body) {
  const response = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    body,
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
    },
  });

  return await response.json();
}

const blogsSection = (blogsPerPage = DEFAULT_BLOGS_PER_PAGE) => ({
  loading: true,
  blogCategories: [],
  articles: [],
  blogsPerPage,
  blogHandle: '',
  pageType: '',
  cursor: '',
  endCursor: '',
  startCursor: '',
  hasPreviousPage: true,
  hasNextPage: true,

  get showAllCategories() {
    return this.pageType === 'multiple';
  },

  initialSetup: async function () {
    let response = null;

    if (this.showAllCategories) {
      const blogCategories = await this.fetchBlogsCategory();
      this.blogCategories = sortAlpha(blogCategories, 'handle');
    } else {
      this.blogCategories = [];
    }

    if (this.blogHandle !== 'all') {
      response = await this.fetchArticlesByHandle(this.blogsPerPage, this.blogHandle, this.cursor);
    } else {
      response = await this.fetchAllArticles(this.blogsPerPage, this.cursor);
    }

    const { articles, endCursor, hasNextPage, hasPreviousPage, startCursor } = response;

    this.endCursor = endCursor;
    this.startCursor = startCursor;
    this.hasPreviousPage = hasPreviousPage;
    this.hasNextPage = hasNextPage;
    this.articles = articles;
  },

  fetchAllArticles: async (blogsPerPage, cursor) => {
    const cursorQuery = cursor ? `after: "${cursor}"` : '';
    let filterQuery = '';

    if (window.hidden_blogs?.length > 0) {
      filterQuery = `query:"tag_not:hide"`;
    }

    const BLOGS_DATA_QUERY = `{
      articles(first: ${blogsPerPage}, ${filterQuery}, ${cursorQuery})
      {
          nodes
          {
              title
              blog{
                  handle
              }
              onlineStoreUrl
              publishedAt
              excerpt
              handle
              author{
                  name
              }
              image{
                url(transform:{maxHeight: 320}),
                altText
              }
          }
          pageInfo
          {
              endCursor,
              hasNextPage
              hasPreviousPage
              startCursor
          }
      }
  }`;

    const { data } = await fetchPost(
      JSON.stringify({
        query: BLOGS_DATA_QUERY,
      }),
    );

    const { pageInfo } = data.articles;

    const articles = data.articles.nodes.map((article) => {
      return {
        title: article.title,
        blogHandle: article.blog.handle,
        articleHandle: article.handle,
        url: article.onlineStoreUrl,
        publishedAt: article.publishedAt,
        author: article.author.name,
        image: article.image,
      };
    });

    return { articles, ...pageInfo };
  },

  fetchArticlesByHandle: async (blogsPerPage, blogHandle, cursor) => {
    const cursorQuery = cursor ? `after: "${cursor}"` : '';

    const BLOGS_DATA_QUERY = /* GraphQL */ `{
      blog(handle:"${blogHandle}"){
          articles(first : ${blogsPerPage},${cursorQuery}){
            nodes{
              title
              blog{
                  handle
              }
              onlineStoreUrl
              publishedAt
              excerpt
              handle
              author{
                  name
              }
              image{
                url(transform:{maxHeight: 320}),
                altText
              }
            }
            pageInfo{
              endCursor,
              hasNextPage
              hasPreviousPage
              startCursor
            }
          }
        }
      }`;

    const {
      data: { blog },
    } = await fetchPost(
      JSON.stringify({
        query: BLOGS_DATA_QUERY,
      }),
    );

    const { pageInfo } = blog.articles;

    const articles = blog.articles.nodes.map((node) => {
      return {
        title: node.title,
        blogHandle: blog.handle,
        articleHandle: node.handle,
        url: node.onlineStoreUrl,
        publishedAt: node.publishedAt,
        author: node.author.name,
        image: node.image,
      };
    });

    return { articles, ...pageInfo };
  },

  fetchBlogsCategory: async () => {
    let filterQuery = '';
    if (window.hidden_blogs?.length > 0) {
      window.hidden_blogs.forEach((element, index) => {
        filterQuery += `-handle:'${element}'`;

        if (index < window.hidden_blogs.length - 1) filterQuery += ' AND ';
      });

      filterQuery = `query:"${filterQuery}"`;
    }

    const BLOGS_DATA_QUERY = /* GraphQL */ `{
        blogs(first: 10, ${filterQuery}) {
          edges {
            node {
              title
              handle
            }
          }
        }
      }`;

    const {
      data: { blogs },
    } = await fetchPost(
      JSON.stringify({
        query: BLOGS_DATA_QUERY,
      }),
    );

    return blogs.edges.map((edge) => {
      return {
        handle: edge.node.handle,
        title: edge.node.title,
      };
    });
  },

  goNextPage() {
    const params =
      '?' +
      new URLSearchParams({
        page: this.page + 1,
        cursor: this.endCursor,
      }).toString();
    window.location.href = window.location.pathname + params;
  },

  goPreviousPage() {
    let params = '';
    if (this.page > 2)
      params =
        '?' +
        new URLSearchParams({
          page: this.page - 1,
          cursor: this.startCursor,
        }).toString();
    window.location.href = window.location.pathname + params;
  },

  isActiveRoute(handle) {
    return handle === this.blogHandle;
  },

  getDate(dateString) {
    const date = new Date(dateString);

    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.toLocaleString('default', { year: 'numeric' });
    const day = date.toLocaleString('default', { day: 'numeric' });

    return `${month} ${day}, ${year}`;
  },

  formatDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  },

  init: async function () {
    this.pageType = window.category_page_type;

    this.blogHandle = window.location.pathname.split('/')[2];

    this.page = new URLSearchParams(window.location.search).get('page')
      ? Number(new URLSearchParams(window.location.search).get('page'))
      : 1;

    this.cursor = new URLSearchParams(window.location.search).get('cursor') ?? '';
    await this.initialSetup();
    this.loading = false;
  },
});

export default blogsSection;
